import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  User,
  GitBranch,
  RotateCcw,
  Eye,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Check,
  Edit3,
  Upload,
  Shield,
  Database,
  ArrowLeft,
  FileText,
  Diff
} from 'lucide-react';
import { useVersionHistory, useVersionComparison } from '../../hooks/useVersionHistory';
import {
  MediaVersion,
  VersionComparison,
  RollbackForm,
  ChangeType
} from '../../types/mediaRecord';

interface VersionHistoryProps {
  mediaId: string;
  className?: string;
}

const getChangeTypeIcon = (changeType: ChangeType) => {
  switch (changeType) {
    case 'edit': return Edit3;
    case 'upload': return Upload;
    case 'security': return Shield;
    case 'metadata': return Database;
    case 'rollback': return RotateCcw;
    default: return FileText;
  }
};

const getChangeTypeColor = (changeType: ChangeType) => {
  switch (changeType) {
    case 'edit': return 'text-blue-400 bg-blue-400/10';
    case 'upload': return 'text-green-400 bg-green-400/10';
    case 'security': return 'text-red-400 bg-red-400/10';
    case 'metadata': return 'text-purple-400 bg-purple-400/10';
    case 'rollback': return 'text-orange-400 bg-orange-400/10';
    default: return 'text-gray-400 bg-gray-400/10';
  }
};

interface VersionItemProps {
  version: MediaVersion;
  isLatest: boolean;
  onRollback: (version: string) => void;
  onCompare: (versionA: string, versionB: string) => void;
  onViewDetails: (version: MediaVersion) => void;
  isRollingBack: boolean;
  selectedForComparison: string[];
  onToggleComparison: (versionId: string) => void;
}

const VersionItem: React.FC<VersionItemProps> = ({
  version,
  isLatest,
  onRollback,
  onCompare,
  onViewDetails,
  isRollingBack,
  selectedForComparison,
  onToggleComparison
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const IconComponent = getChangeTypeIcon(version.change_type);
  const isSelected = selectedForComparison.includes(version.id);

  const handleRollback = () => {
    if (confirm(`Are you sure you want to rollback to version ${version.version}? This action cannot be undone.`)) {
      onRollback(version.version);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className={`relative ${isSelected ? 'ring-2 ring-blue-400' : ''}`}
    >
      {/* Timeline connector */}
      <div className="absolute left-6 top-12 bottom-0 w-px bg-gradient-to-b from-white/20 to-transparent"></div>
      
      <div className="cosmic-glass rounded-lg overflow-hidden mb-4">
        <div className="p-4">
          <div className="flex items-start gap-4">
            {/* Version indicator */}
            <div className="flex-shrink-0 relative">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getChangeTypeColor(version.change_type)}`}>
                <IconComponent className="w-5 h-5" />
              </div>
              {isLatest && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-2 h-2 text-white" />
                </div>
              )}
            </div>

            {/* Version content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white">Version {version.version}</h3>
                    {isLatest && (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                        Current
                      </span>
                    )}
                    <span className={`px-2 py-1 text-xs rounded-full ${getChangeTypeColor(version.change_type)}`}>
                      {version.change_type}
                    </span>
                  </div>
                  
                  <p className="text-gray-300 text-sm mb-2">{version.description}</p>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {version.author.name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(version.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onToggleComparison(version.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      isSelected 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white/10 text-gray-400 hover:text-white'
                    }`}
                    title="Select for comparison"
                  >
                    <Diff className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => onViewDetails(version)}
                    className="p-2 bg-white/10 text-gray-400 hover:text-white rounded-lg transition-colors"
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  {!isLatest && (
                    <button
                      onClick={handleRollback}
                      disabled={isRollingBack}
                      className="p-2 bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 rounded-lg transition-colors disabled:opacity-50"
                      title="Rollback to this version"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-2 bg-white/10 text-gray-400 hover:text-white rounded-lg transition-colors"
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Expanded details */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-4 pt-4 border-t border-white/10 overflow-hidden"
                  >
                    {/* Tags */}
                    {version.tags.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Tags</h4>
                        <div className="flex flex-wrap gap-1">
                          {version.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-600/20 text-blue-300 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Changes */}
                    {version.changes.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Changes</h4>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {version.changes.map((change, index) => (
                            <div key={index} className="text-sm bg-white/5 rounded p-2">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-white">{change.field}</span>
                                <span className="text-xs text-gray-400">
                                  {new Date(change.timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-red-400">- {JSON.stringify(change.oldValue)}</span>
                                </div>
                                <div>
                                  <span className="text-green-400">+ {JSON.stringify(change.newValue)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Content hash */}
                    <div className="text-xs">
                      <span className="text-gray-400">Content Hash: </span>
                      <span className="font-mono text-gray-300">
                        {version.content_hash.substring(0, 32)}...
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface ComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  comparison: VersionComparison | null;
  isLoading: boolean;
}

const ComparisonModal: React.FC<ComparisonModalProps> = ({
  isOpen,
  onClose,
  comparison,
  isLoading
}) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="cosmic-glass rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Version Comparison</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Comparing versions...</p>
            </div>
          ) : comparison ? (
            <div className="space-y-6">
              {/* Version headers */}
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <h4 className="font-semibold text-white mb-2">Version {comparison.version_a.version}</h4>
                  <div className="text-sm text-gray-400">
                    <p>{comparison.version_a.author.name}</p>
                    <p>{new Date(comparison.version_a.created_at).toLocaleString()}</p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs mt-1 ${getChangeTypeColor(comparison.version_a.change_type)}`}>
                      {comparison.version_a.change_type}
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <h4 className="font-semibold text-white mb-2">Version {comparison.version_b.version}</h4>
                  <div className="text-sm text-gray-400">
                    <p>{comparison.version_b.author.name}</p>
                    <p>{new Date(comparison.version_b.created_at).toLocaleString()}</p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs mt-1 ${getChangeTypeColor(comparison.version_b.change_type)}`}>
                      {comparison.version_b.change_type}
                    </span>
                  </div>
                </div>
              </div>

              {/* Differences */}
              <div className="space-y-4">
                {/* Added */}
                {Object.keys(comparison.differences.added).length > 0 && (
                  <div>
                    <h5 className="font-medium text-green-400 mb-2">Added</h5>
                    <div className="bg-green-400/10 rounded-lg p-3">
                      {Object.entries(comparison.differences.added).map(([key, value]) => (
                        <div key={key} className="text-sm mb-1">
                          <span className="font-medium text-white">{key}:</span>
                          <span className="text-green-400 ml-2">{JSON.stringify(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Removed */}
                {Object.keys(comparison.differences.removed).length > 0 && (
                  <div>
                    <h5 className="font-medium text-red-400 mb-2">Removed</h5>
                    <div className="bg-red-400/10 rounded-lg p-3">
                      {Object.entries(comparison.differences.removed).map(([key, value]) => (
                        <div key={key} className="text-sm mb-1">
                          <span className="font-medium text-white">{key}:</span>
                          <span className="text-red-400 ml-2">{JSON.stringify(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Modified */}
                {Object.keys(comparison.differences.modified).length > 0 && (
                  <div>
                    <h5 className="font-medium text-yellow-400 mb-2">Modified</h5>
                    <div className="bg-yellow-400/10 rounded-lg p-3">
                      {Object.entries(comparison.differences.modified).map(([key, value]) => (
                        <div key={key} className="text-sm mb-2">
                          <span className="font-medium text-white">{key}:</span>
                          <div className="mt-1 grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-red-400">- {JSON.stringify(value.old)}</span>
                            </div>
                            <div>
                              <span className="text-green-400">+ {JSON.stringify(value.new)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                {comparison.changes_timeline.length > 0 && (
                  <div>
                    <h5 className="font-medium text-blue-400 mb-2">Changes Timeline</h5>
                    <div className="bg-blue-400/10 rounded-lg p-3 max-h-48 overflow-y-auto">
                      {comparison.changes_timeline.map((change, index) => (
                        <div key={index} className="text-sm mb-2 pb-2 border-b border-white/10 last:border-b-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-white">{change.field}</span>
                            <span className="text-xs text-gray-400">{change.source}</span>
                            <span className="text-xs text-gray-400">
                              {new Date(change.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-red-400">Old: {JSON.stringify(change.oldValue)}</span>
                            </div>
                            <div>
                              <span className="text-green-400">New: {JSON.stringify(change.newValue)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-8">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Failed to load comparison data</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export const VersionHistory: React.FC<VersionHistoryProps> = ({ mediaId, className }) => {
  const {
    data: versionHistory,
    isLoading,
    error,
    rollback,
    compare,
    isRollingBack
  } = useVersionHistory(mediaId, {
    limit: 50,
    includeDeleted: false
  });

  const { compareVersions, isComparing, comparisonResult } = useVersionComparison();

  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [currentComparison, setCurrentComparison] = useState<VersionComparison | null>(null);

  const handleRollback = async (targetVersion: string) => {
    try {
      await rollback({
        target_version: targetVersion,
        reason: `Rollback to version ${targetVersion} via UI`
      });
    } catch (error) {
      console.error('Rollback failed:', error);
    }
  };

  const handleToggleComparison = (versionId: string) => {
    setSelectedForComparison(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else if (prev.length < 2) {
        return [...prev, versionId];
      } else {
        return [prev[1], versionId]; // Replace first selection
      }
    });
  };

  const handleCompare = async () => {
    if (selectedForComparison.length === 2 && versionHistory) {
      const versionA = versionHistory.versions.find(v => v.id === selectedForComparison[0]);
      const versionB = versionHistory.versions.find(v => v.id === selectedForComparison[1]);
      
      if (versionA && versionB) {
        try {
          const result = await compareVersions(mediaId, versionA.version, versionB.version);
          setCurrentComparison(result);
          setShowComparison(true);
        } catch (error) {
          console.error('Comparison failed:', error);
        }
      }
    }
  };

  const handleViewDetails = (version: MediaVersion) => {
    // Could open a detailed view or expand inline
    console.log('View details for version:', version);
  };

  if (isLoading) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-white/10 rounded w-3/4"></div>
                  <div className="h-3 bg-white/10 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !versionHistory) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="text-center text-red-400">
          <GitBranch className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Failed to load version history</p>
          <p className="text-sm opacity-75">{error?.message || 'Unknown error occurred'}</p>
        </div>
      </div>
    );
  }

  const { versions, stats } = versionHistory;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`space-y-6 ${className}`}
    >
      {/* Header */}
      <div className="cosmic-glass rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Version History</h2>
          </div>
          
          {selectedForComparison.length === 2 && (
            <button
              onClick={handleCompare}
              disabled={isComparing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <Diff className="w-4 h-4" />
              {isComparing ? 'Comparing...' : 'Compare Selected'}
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{stats.total_versions}</div>
            <div className="text-xs text-gray-400">Total Versions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{stats.unique_authors}</div>
            <div className="text-xs text-gray-400">Contributors</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">{stats.edit_count}</div>
            <div className="text-xs text-gray-400">Edits</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">{stats.rollback_count}</div>
            <div className="text-xs text-gray-400">Rollbacks</div>
          </div>
        </div>

        {selectedForComparison.length > 0 && (
          <div className="mt-4 p-3 bg-blue-400/10 rounded-lg">
            <p className="text-sm text-blue-300">
              {selectedForComparison.length} version{selectedForComparison.length > 1 ? 's' : ''} selected for comparison
              {selectedForComparison.length === 2 && ' - click Compare Selected to view differences'}
            </p>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="space-y-0">
        {versions.map((version, index) => (
          <VersionItem
            key={version.id}
            version={version}
            isLatest={index === 0}
            onRollback={handleRollback}
            onCompare={compare}
            onViewDetails={handleViewDetails}
            isRollingBack={isRollingBack}
            selectedForComparison={selectedForComparison}
            onToggleComparison={handleToggleComparison}
          />
        ))}
      </div>

      {/* Pagination if needed */}
      {versionHistory.pagination.total > versionHistory.pagination.limit && (
        <div className="cosmic-glass rounded-lg p-4 text-center">
          <p className="text-gray-400">
            Showing {versionHistory.pagination.limit} of {versionHistory.pagination.total} versions
          </p>
          <button className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
            Load More
          </button>
        </div>
      )}

      {/* Comparison Modal */}
      <AnimatePresence>
        <ComparisonModal
          isOpen={showComparison}
          onClose={() => {
            setShowComparison(false);
            setCurrentComparison(null);
          }}
          comparison={currentComparison}
          isLoading={isComparing}
        />
      </AnimatePresence>
    </motion.div>
  );
};

export default VersionHistory;